import { CVEditingSection, CVSensitiveInfo, FullCV } from '@cibli/shared';
import { Car, GraduationCap, Mail, MapPin, Navigation, Phone } from 'lucide-react';
import React, { useMemo } from 'react';

import { useCVTranslation } from '@/features/cv/hooks/useCVTranslation.ts';

import EditableSection from '../../../components/form/EditableSection';
import { CVViewMode } from '../../types';
import ProfilePicture from './ProfilePicture';

interface PersonalInfoProps {
  personalInfos: FullCV;
  sensitiveInfo: CVSensitiveInfo;
  onEdit?: (section: CVEditingSection) => void;
  mode?: CVViewMode;
  headingColor?: string;
}

const PersonalInfo: React.FC<PersonalInfoProps> = ({
  personalInfos,
  sensitiveInfo,
  onEdit,
  mode = 'preview',
  headingColor,
}) => {
  const { t } = useCVTranslation();
  const infos = useMemo(() => {
    return {
      ...personalInfos,
      ...sensitiveInfo,
    };
  }, [personalInfos, sensitiveInfo]);

  const driverLicenseLabel = useMemo(() => {
    if (!infos.hasDriverLicense) return '';
    const types = infos.driverLicenseTypes;
    const filled = types != null && types.trim().length > 0;
    const licenseKey = t('CV.drivingLicense');
    if (!filled) return licenseKey;
    const escaped = licenseKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const cleanTypes = types.trim().replace(new RegExp(`^${escaped}\\s*`, 'i'), '');
    return cleanTypes ? `${licenseKey} ${cleanTypes}` : licenseKey;
  }, [infos.hasDriverLicense, infos.driverLicenseTypes, t]);

  const jobTitle = useMemo(() => {
    const title = infos.jobTitle;
    if (!title) return null;
    return title;
  }, [infos.jobTitle]);

  const handleEdit = () => {
    if (onEdit) {
      onEdit('personalInfos');
    }
  };

  return (
    <EditableSection onEdit={handleEdit} editable={onEdit !== undefined} enablePageBreak={false}>
      <div className="p-2 m-2">
        {mode === 'print' && !infos.pictureBase64 ? null : (
          <div className="text-center mb-2">
            <ProfilePicture
              imageBase64={infos.pictureBase64 || null}
              firstName={infos.firstName}
              lastName={infos.lastName}
              size="sm"
              showPlaceholder={mode === 'edit'}
              onClick={handleEdit}
            />
          </div>
        )}
        <h1
          className="text-[15px] font-black uppercase tracking-widest text-center mb-0.25"
          style={{ color: headingColor ?? '#000000' }}
        >
          {infos.firstName} {infos.lastName}
        </h1>
        {jobTitle && <p className="text-[20px] text-center font-bold mb-2">{jobTitle}</p>}

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Phone className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
            <span className="text-[11px] font-semibold leading-relaxed">{infos.phone}</span>
          </div>
          {infos.email && (
            <div className="flex items-center gap-2 break-all">
              <Mail className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed">{infos.email}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <MapPin className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
            <span className="text-[11px] font-semibold leading-relaxed">
              {infos.location} {infos.postalCode}
            </span>
          </div>
          {infos.hasDriverLicense && (
            <div className="flex items-center gap-2">
              <Car className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed">
                {driverLicenseLabel}
              </span>
            </div>
          )}
          {infos.isMobile && (
            <div className="flex items-center gap-2">
              <Navigation className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed">{t('CV.mobile')}</span>
            </div>
          )}
          {infos.openToTraining && (
            <div className="flex items-center gap-2">
              <GraduationCap className="min-h-4 min-w-4 max-h-4 max-w-4 text-black" />
              <span className="text-[11px] font-semibold leading-relaxed">
                {t('Forms.PersonalInfo.openToTraining')}
              </span>
            </div>
          )}
        </div>
      </div>
    </EditableSection>
  );
};

export default PersonalInfo;
